import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Tag,
  Alert,
  Popconfirm,
  message,
  Space,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import { useAdmin } from "context/adminContext";
import { useLoading } from "context/loadingContext";
import BankCodeSelect from "components/select/bankCodeSelect";
import { bg_button, bg_shadow } from "constant/css";
import {
  ListFeeShareRules,
  CreateFeeShareRule,
  UpdateFeeShareRule,
  DeleteFeeShareRule,
} from "functions/admin/feeShareRule";

const FeeShareRulePanel = () => {
  const { activeMerchantId } = useAdmin();
  const { showLoading, hideLoading } = useLoading();
  const [rules, setRules] = useState([]);
  const [summary, setSummary] = useState({
    totalActivePercent: 0,
    isValid: false,
    activeCount: 0,
    willSplit: false,
  });
  const [modal, setModal] = useState({ open: false, record: null });
  const [form] = Form.useForm();

  useEffect(() => {
    if (activeMerchantId) fetchRules();
  }, [activeMerchantId]);

  const fetchRules = async () => {
    showLoading();
    try {
      const response = await ListFeeShareRules(activeMerchantId);
      if (response) {
        setRules(response.data || []);
        setSummary(
          response.summary || {
            totalActivePercent: 0,
            isValid: false,
            activeCount: 0,
            willSplit: false,
          }
        );
      }
    } finally {
      hideLoading();
    }
  };

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ active: true, percent: 0 });
    setModal({ open: true, record: null });
  };

  const openEdit = (record) => {
    form.setFieldsValue({
      recipientName: record.recipientName,
      accountNumber: record.accountNumber,
      bankCode: record.bankCode,
      percent: parseFloat(record.percent),
      active: record.active,
      description: record.description,
    });
    setModal({ open: true, record });
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      showLoading();
      const payload = {
        ...values,
        merchantId: activeMerchantId,
      };
      let response;
      if (modal.record) {
        response = await UpdateFeeShareRule(modal.record.id, payload);
      } else {
        response = await CreateFeeShareRule(payload);
      }
      if (response && response.success) {
        message.success(
          modal.record ? "Дүрэм шинэчлэгдлээ" : "Дүрэм нэмэгдлээ"
        );
        setModal({ open: false, record: null });
        fetchRules();
      }
    } catch (e) {
      // validate fields error
    } finally {
      hideLoading();
    }
  };

  const remove = async (id) => {
    showLoading();
    try {
      const response = await DeleteFeeShareRule(id);
      if (response && response.success) {
        message.success("Дүрэм устгагдлаа");
        fetchRules();
      }
    } finally {
      hideLoading();
    }
  };

  const toggleActive = async (record) => {
    showLoading();
    try {
      const response = await UpdateFeeShareRule(record.id, {
        active: !record.active,
      });
      if (response && response.success) {
        fetchRules();
      }
    } finally {
      hideLoading();
    }
  };

  const columns = [
    { title: "#", dataIndex: "id", width: 60 },
    {
      title: "Хүлээн авагч",
      dataIndex: "recipientName",
      render: (v) => v || "-",
    },
    { title: "Данс", dataIndex: "accountNumber" },
    { title: "Банкны код", dataIndex: "bankCode", width: 110 },
    {
      title: "Хувь",
      dataIndex: "percent",
      width: 90,
      align: "right",
      render: (v) => <Tag color="blue">{parseFloat(v)}%</Tag>,
    },
    {
      title: "Идэвхтэй",
      dataIndex: "active",
      width: 100,
      render: (v, r) => (
        <Switch checked={v} onChange={() => toggleActive(r)} size="small" />
      ),
    },
    {
      title: "Тайлбар",
      dataIndex: "description",
      render: (v) => v || "-",
    },
    {
      title: "Үйлдэл",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Устгах уу?"
            okText="Тийм"
            cancelText="Үгүй"
            onConfirm={() => remove(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderStatus = () => {
    if (summary.activeCount === 0) {
      return (
        <Alert
          type="info"
          showIcon
          message="Тооцоо хуваарилалт идэвхгүй"
          description="Идэвхтэй дүрэм байхгүй тул худалдаачны өндөрлөгөө хуучин процессоор (бүгд merchant дансанд) ажиллана."
        />
      );
    }
    if (!summary.isValid) {
      return (
        <Alert
          type="warning"
          showIcon
          message={`Анхаар: идэвхтэй дүрмүүдийн нийлбэр = ${summary.totalActivePercent}% (100% байх ёстой)`}
          description="100% болоогүй тул өндөрлөгөө хуучин процессоор (бүгд merchant дансанд) ажиллана. Settle хийгдэх үед админд имэйл алдаа очно."
        />
      );
    }
    return (
      <Alert
        type="success"
        showIcon
        message={`Тооцоо хуваарилалт идэвхтэй (${summary.activeCount} дүрэм, 100%)`}
        description="Settle хийх үед merchantFee нь дүрмүүдийн дагуу хуваагдаж тус тусын данс руу шилжих болно."
      />
    );
  };

  if (!activeMerchantId) {
    return <Alert type="warning" message="Merchant сонгоно уу" />;
  }

  return (
    <div className={`p-4 bg-white rounded-xl ${bg_shadow}`}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <h2 className="font-semibold text-lg">Тооцоо хуваарилалтын дүрэм</h2>
          <span className="text-xs text-gray-500">
            Settle үед merchantFee-г олон хүлээн авагч руу хуваах
          </span>
        </Col>
        <Col>
          <Space>
            <Button icon={<RedoOutlined />} onClick={fetchRules} />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              className={`${bg_button}`}
            >
              Дүрэм нэмэх
            </Button>
          </Space>
        </Col>
      </Row>

      <div style={{ marginBottom: 12 }}>{renderStatus()}</div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rules}
        size="small"
        pagination={false}
      />

      <Modal
        title={modal.record ? "Дүрэм засах" : "Дүрэм нэмэх"}
        open={modal.open}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={submit}
        okText="Хадгалах"
        cancelText="Болих"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Хүлээн авагчийн нэр"
            name="recipientName"
            rules={[{ required: true, message: "Нэр оруулна уу" }]}
          >
            <Input placeholder="Жишээ: ХХК А" />
          </Form.Item>
          <Form.Item
            label="Дансны дугаар"
            name="accountNumber"
            rules={[{ required: true, message: "Данс оруулна уу" }]}
          >
            <Input placeholder="50000000000" />
          </Form.Item>
          <Form.Item
            label="Банк"
            name="bankCode"
            rules={[{ required: true, message: "Банк сонгоно уу" }]}
          >
            <BankCodeSelect />
          </Form.Item>
          <Form.Item
            label="Хувь (%)"
            name="percent"
            rules={[
              { required: true, message: "Хувь оруулна уу" },
              {
                type: "number",
                min: 0.01,
                max: 100,
                message: "0.01 - 100 хооронд",
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={100}
              step={0.01}
              precision={2}
            />
          </Form.Item>
          <Form.Item label="Идэвхтэй" name="active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Тайлбар" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FeeShareRulePanel;
